<template>
  <LlScrollableBlock fixedButton noFixed :title="$t(spare.title)">
    <template v-slot:fixed-button>
      <WsButton001
        v-if="!isLoading.content"
        narrow
        flatButton
        leadingIcon="icon-edit-pencil"
        @click="$_onOpenEditPopup()"
      >
        {{ $t(spare.editButtonText) }}
      </WsButton001>
    </template>
    <LlScrollableBlockContent v-if="isLoading.content">
      <NormalSkeleton />
    </LlScrollableBlockContent>
    <LlScrollableBlockContent v-else isAutoHeight>
      <LlAvatar
        v-if="content.updatedUser"
        bold
        timeLabel="編輯時間"
        :circleSize="36"
        :time="content.updatedAt"
        :image="content.updatedUser.avatar"
        :textLabel="content.updatedUser.name"
        class="mb-20"
      />
      <div class="lv-backup-setting__card mb-16">
        <LvFlexBox noWrap alignItems="center">
          <LvIcon
            :icon="spare.nextBackupCard.icon"
            size="18px"
            class="lv-backup-setting__card-icon"
          />
          <LvText h6 lightGray>{{ $t(spare.nextBackupCard.title) }}</LvText>
        </LvFlexBox>
        <LvText h2 bold class="mt-8">{{ content.nextBackupAt || $t(spare.emptyText) }}</LvText>
      </div>
      <div class="lv-backup-setting__cards">
        <div v-for="card in spare.cards" :key="card.id" class="lv-backup-setting__card">
          <LvFlexBox noWrap alignItems="center">
            <LvIcon :icon="card.icon" size="18px" class="lv-backup-setting__card-icon" />
            <LvText h6 lightGray>{{ $t(card.title) }}</LvText>
          </LvFlexBox>
          <LvFlexBox noWrap alignItems="baseline" class="mt-8">
            <LvText h2 bold>{{ $_getValueText(card.id) }}</LvText>
            <LvText v-if="card.unit && content[card.id] !== null" h6 lightGray>
              {{ $t(card.unit) }}
            </LvText>
          </LvFlexBox>
          <LvFlexBox v-if="card.warning" noWrap alignItems="center" class="mt-8">
            <LvIcon icon="icon-warning" size="14px" class="lv-backup-setting__warning-icon" />
            <LvText h6 red>{{ $t(card.warning) }}</LvText>
          </LvFlexBox>
          <LvText v-else h6 lightGray class="mt-8">{{ $t(card.description) }}</LvText>
        </div>
      </div>
    </LlScrollableBlockContent>
    <PopUp
      bottom
      :clickOutsideClose="false"
      :popUpActive="isActive.editPopup"
      :title="$t(spare.popupTitle)"
      @click="$_onCloseEditPopup()"
    >
      <ValidationObserver ref="formObserver" tag="div" class="lv-backup-setting__popup-list">
        <div v-for="card in spare.cards" :key="card.id" class="lv-backup-setting__popup-row">
          <div class="lv-backup-setting__popup-row-info">
            <LvText h5 bold>{{ $t(card.title) }}</LvText>
            <LvFlexBox v-if="card.warning" noWrap alignItems="center" class="mt-4">
              <LvIcon icon="icon-warning" size="14px" class="lv-backup-setting__warning-icon" />
              <LvText h6 red>{{ $t(card.warning) }}</LvText>
            </LvFlexBox>
            <LvText v-else h6 lightGray class="mt-4">{{ $t(card.description) }}</LvText>
          </div>
          <div class="lv-backup-setting__popup-row-input">
            <TimePicker
              v-if="card.type === 'time'"
              :value="_backupTimeDate"
              :clearable="false"
              rules="required"
              @input="$_onInputBackupTime($event)"
            />
            <LvFlexBox v-else noWrap alignItems="center">
              <LvNumberStepper v-model="form[card.id]" :min="1" rules="required" />
              <LvText h5 noWrap>{{ $t(card.unit) }}</LvText>
            </LvFlexBox>
          </div>
        </div>
      </ValidationObserver>
      <template slot="bottomBtn">
        <LvFlexBox
          v-if="_crossFieldErrorTexts.length"
          flexDirection="column"
          alignItems="flex-start"
        >
          <LvFlexBox
            v-for="errorText in _crossFieldErrorTexts"
            :key="errorText"
            noWrap
            alignItems="center"
          >
            <LvIcon icon="icon-warning" size="14px" class="lv-backup-setting__warning-icon" />
            <LvText h6 red minWidth0>{{ $t(errorText) }}</LvText>
          </LvFlexBox>
        </LvFlexBox>
        <WsButton001
          outLine
          widthStretch
          :isloading="isLoading.submit"
          @click="$_onCloseEditPopup()"
        >
          {{ $t('取消') }}
        </WsButton001>
        <WsButton001 widthStretch :isloading="isLoading.submit" @click="$_onSubmit()">
          {{ $t('儲存') }}
        </WsButton001>
      </template>
    </PopUp>
  </LlScrollableBlock>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import S_backupSetting from '@/service/api/v1/backupSetting';
import H_backupSetting from '@/helpers/backupSetting';
import Spare_backupSetting from '@/spare/backupSetting';

export default {
  components: {
    ValidationObserver,
  },
  data() {
    return {
      spare: Spare_backupSetting,
      isLoading: {
        content: true,
        submit: false,
      },
      isActive: {
        editPopup: false,
      },
      content: {
        id: null,
        retentionDays: null,
        backupFrequencyDays: null,
        backupDays: null,
        backupTime: null,
        nextBackupAt: null,
        updatedAt: '',
        updatedUser: null,
      },
      form: {
        retentionDays: null,
        backupFrequencyDays: null,
        backupDays: null,
        backupTime: null,
      },
    };
  },
  computed: {
    // 備份範圍需介於備份頻率與紀錄保留天數之間；任一欄為空時不判斷，交給各欄位的 required
    _crossFieldErrorTexts() {
      const { retentionDays, backupFrequencyDays, backupDays } = this.form;
      const errorTexts = [];
      if (backupDays === null) return errorTexts;
      if (retentionDays !== null && backupDays > retentionDays) {
        errorTexts.push(this.spare.backupDaysMaxErrorText);
      }
      if (backupFrequencyDays !== null && backupDays < backupFrequencyDays) {
        errorTexts.push(this.spare.backupDaysMinErrorText);
      }
      return errorTexts;
    },
    // TimePicker 內部用 new Date(value)，直接給字串在 Safari 會解析失敗，所以先轉成 Date
    _backupTimeDate() {
      return this.form.backupTime ? this.$moment(this.form.backupTime, 'HH:mm').toDate() : null;
    },
  },
  async mounted() {
    await this.$_init();
  },
  methods: {
    async $_init() {
      this.isLoading.content = true;
      try {
        const res = await S_backupSetting.show();
        this.content = H_backupSetting.setShow(res);
      } catch (error) {
        alert(this.$t(this.spare.showErrorText));
      } finally {
        this.isLoading.content = false;
      }
    },
    $_getValueText(fieldId) {
      const value = this.content[fieldId];
      return value === null ? this.$t(this.spare.emptyText) : value;
    },
    $_onOpenEditPopup() {
      const { retentionDays, backupFrequencyDays, backupDays, backupTime } = this.content;
      this.form = { retentionDays, backupFrequencyDays, backupDays, backupTime };
      this.isActive.editPopup = true;
      this.$nextTick(() => this.$refs.formObserver.reset());
    },
    $_onInputBackupTime($event) {
      this.form.backupTime = $event ? $event.format('HH:mm') : null;
    },
    $_onCloseEditPopup() {
      if (this.isLoading.submit) return;
      this.isActive.editPopup = false;
    },
    async $_onSubmit() {
      const isValidate = await this.$refs.formObserver.validate();
      if (!isValidate || this._crossFieldErrorTexts.length) return;
      this.isLoading.submit = true;
      try {
        await S_backupSetting.update(this.content.id, H_backupSetting.setUpdateData(this.form));
        this.isActive.editPopup = false;
        this.$store.dispatch('snackbar/openSnackbar', {
          content: this.$t(this.spare.updateSuccessText),
        });
        await this.$_init();
      } catch (error) {
        alert(this.$t(this.spare.updateErrorText));
      } finally {
        this.isLoading.submit = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.lv-backup-setting__cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 1199px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
}
.lv-backup-setting__card {
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  padding: 20px;
  background: var(--white);
}
.lv-backup-setting__card-icon {
  color: var(--blue-500);
}
.lv-backup-setting__warning-icon {
  color: var(--red-700);
}
.lv-backup-setting__popup-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.lv-backup-setting__popup-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  padding: 16px;
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
}
.lv-backup-setting__popup-row-info {
  min-width: 0;
}
.lv-backup-setting__popup-row-input {
  flex: none;
  width: 172px;
  @media (max-width: 767px) {
    width: 120px;
  }
}
</style>
